import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateProjectInput, ProjectItem, UpdateProjectInput } from '@crm/shared';
import { api } from '../../api/client';

const KEY = ['projects'];
/** Lists that show project labels or counts change when a project is renamed, removed or gets a new logo. */
const LABELLED_KEYS = [KEY, ['tags'], ['lists'], ['segments'], ['campaigns'], ['campaign']];

export const useProjects = () => useQuery({ queryKey: KEY, queryFn: async () => (await api.get<ProjectItem[]>('/projects')).data, staleTime: 60_000 });

function useInvalidate() {
  const qc = useQueryClient();
  return () => LABELLED_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}

export function useCreateProject() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (input: CreateProjectInput) => (await api.post<ProjectItem>('/projects', input)).data, onSuccess: inv });
}

export function useUpdateProject() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async ({ id, ...input }: UpdateProjectInput & { id: string }) => (await api.patch<ProjectItem>(`/projects/${id}`, input)).data, onSuccess: inv });
}

export function useDeleteProject() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/projects/${id}`)).data, onSuccess: inv });
}

/** Full logo (kept in its proportions as JPEG/PNG so emails can show it) or square icon (selector and labels). */
export type ProjectImage = 'logo' | 'icon';

/** Uploads (a File) or removes (null) a project's full logo or icon. */
export function useProjectImage() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, kind, file }: { id: string; kind: ProjectImage; file: File | null }) => {
      if (!file) return (await api.delete<ProjectItem>(`/projects/${id}/${kind}`)).data;
      const form = new FormData();
      form.append('file', file);
      return (await api.post<ProjectItem>(`/projects/${id}/${kind}`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
    onSuccess: inv,
  });
}
